/**
 * LocationActivityTest - Test component for SignalR Location Activity
 * 
 * Demonstrates usage of useLocationActivity hook and ConnectionStatus component.
 * Useful for testing and debugging SignalR connection and event handling.
 * 
 * @author BizClick WMS
 * @version 1.0 - Fase 2 Implementation
 */

import React, { useState, useCallback } from 'react';
import { useLocationActivity } from '../../hooks/useLocationActivity';
import { ConnectionStatus as ConnectionStatusComponent } from './ConnectionStatus';
import { 
  LocationActivityEvent, 
  ActivityType, 
  ActivityTypeNames,
  ActivityTypeColors 
} from '../../types/locationActivity';
import { Activity, Trash2 } from 'lucide-react';

/**
 * LocationActivityTest Component
 */
export const LocationActivityTest: React.FC = () => {
  const [events, setEvents] = useState<LocationActivityEvent[]>([]);
  const [warehouseInput, setWarehouseInput] = useState('');
  const [rackInput, setRackInput] = useState('');
  const [joinedGroups, setJoinedGroups] = useState<string[]>([]);
  
  // Handle incoming activity events
  const handleActivityReceived = useCallback((event: LocationActivityEvent) => {
    console.log('[LocationActivityTest] New event:', event);
    setEvents(prev => [event, ...prev].slice(0, 20)); // Keep last 20 events
  }, []);
  
  // Handle connection state changes
  const handleConnectionStateChanged = useCallback((status: string) => {
    console.log('[LocationActivityTest] Connection status changed:', status);
  }, []);
  
  // Initialize SignalR hook
  const {
    connectionStatus,
    connectionQuality,
    lastEvent,
    joinWarehouseGroup,
    joinRackGroup,
    leaveGroup,
    disconnect
  } = useLocationActivity(handleActivityReceived, handleConnectionStateChanged);
  
  /**
   * Join warehouse group handler
   */
  const handleJoinWarehouse = async () => {
    if (!warehouseInput.trim()) return;
    
    try {
      await joinWarehouseGroup(warehouseInput.trim());
      setJoinedGroups(prev => [...prev, `Warehouse:${warehouseInput.trim()}`]);
      setWarehouseInput('');
    } catch (error) {
      console.error('Failed to join warehouse group:', error);
      alert('Feil ved tilkobling til lagergruppe: ' + error);
    }
  };
  
  /**
   * Join rack group handler
   */
  const handleJoinRack = async () => {
    const rackNumber = parseInt(rackInput);
    if (isNaN(rackNumber) || !warehouseInput.trim()) return;
    
    try {
      await joinRackGroup(warehouseInput.trim(), rackNumber);
      setJoinedGroups(prev => [...prev, `Rack:${warehouseInput.trim()}/${rackNumber}`]);
      setRackInput('');
    } catch (error) {
      console.error('Failed to join rack group:', error);
      alert('Feil ved tilkobling til reolgruppe: ' + error);
    }
  };
  
  /**
   * Leave group handler
   */
  const handleLeaveGroup = async (groupName: string) => {
    try {
      await leaveGroup(groupName);
      setJoinedGroups(prev => prev.filter(g => g !== groupName));
    } catch (error) {
      console.error('Failed to leave group:', error);
      alert('Feil ved frakobling fra gruppe: ' + error);
    }
  };
  
  /**
   * Clear events handler
   */
  const handleClearEvents = () => {
    setEvents([]);
  };
  
  /**
   * Manual disconnect handler
   */
  const handleDisconnect = () => {
    disconnect();
    setJoinedGroups([]);
  };
  
  /**
   * Format timestamp for display
   */
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('nb-NO');
  };
  
  /**
   * Get activity type badge color
   */
  const getActivityTypeBadge = (activityType: ActivityType): string => {
    const color = ActivityTypeColors[activityType];
    const name = ActivityTypeNames[activityType];
    
    return `${color} px-2 py-1 rounded text-xs font-medium`;
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800">
            Live Location Activity Test
          </h1>
        </div>
        <button
          onClick={handleDisconnect}
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
        >
          Koble fra
        </button>
      </div>
      
      {/* Connection Status */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Tilkoblingsstatus</h2>
        <ConnectionStatusComponent status={connectionStatus} quality={connectionQuality} />
      </div>
      
      {/* Group Management */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Grupper</h2>
        
        {/* Join Warehouse Group */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Koble til lagergruppe
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={warehouseInput}
              onChange={(e) => setWarehouseInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleJoinWarehouse()}
              placeholder="Lager-ID (f.eks. WH01)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleJoinWarehouse}
              disabled={!warehouseInput.trim()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Koble til
            </button>
          </div>
        </div>
        
        {/* Join Rack Group */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Koble til reolgruppe (krever lager-ID først)
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              value={rackInput}
              onChange={(e) => setRackInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleJoinRack()}
              placeholder="Reol-nummer (f.eks. 5)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleJoinRack}
              disabled={!rackInput || isNaN(parseInt(rackInput)) || !warehouseInput.trim()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Koble til
            </button>
          </div>
          {!warehouseInput.trim() && (
            <p className="text-xs text-amber-600 mt-1">
              ⚠️ Du må angi et lager-ID først
            </p>
          )}
        </div>
        
        {/* Joined Groups List */}
        {joinedGroups.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Tilkoblede grupper:</h3>
            <div className="flex flex-wrap gap-2">
              {joinedGroups.map(group => (
                <div
                  key={group}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full"
                >
                  <span className="text-sm font-medium">{group}</span>
                  <button
                    onClick={() => handleLeaveGroup(group)}
                    className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Last Event */}
      {lastEvent && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Siste hendelse</h2>
          <div className="bg-gray-50 rounded p-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-600">Lokasjon:</span>
                <span className="ml-2 text-gray-800">{lastEvent.binCode}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Reol:</span>
                <span className="ml-2 text-gray-800">{lastEvent.rackNumber}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Type:</span>
                <span className={`ml-2 ${getActivityTypeBadge(lastEvent.activityType)}`}>
                  {ActivityTypeNames[lastEvent.activityType]}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Vare:</span>
                <span className="ml-2 text-gray-800">{lastEvent.itemNo}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Antall:</span>
                <span className="ml-2 text-gray-800">{lastEvent.quantity}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Operatør:</span>
                <span className="ml-2 text-gray-800">{lastEvent.operatorId}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Event Log */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">
            Hendelseslogg ({events.length})
          </h2>
          <button
            onClick={handleClearEvents}
            disabled={events.length === 0}
            className="px-3 py-1.5 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Tøm logg
          </button>
        </div>
        
        {events.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Ingen hendelser mottatt ennå. Vent på aktivitet eller koble til en gruppe.
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {events.map((event, index) => (
              <div
                key={`${event.timestamp}-${index}`}
                className="p-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={getActivityTypeBadge(event.activityType)}>
                    {ActivityTypeNames[event.activityType]}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatTimestamp(event.timestamp)}
                  </span>
                </div>
                <div className="text-sm text-gray-700">
                  <span className="font-medium">{event.binCode}</span>
                  {' - '}
                  <span>{event.itemNo}</span>
                  {' - '}
                  <span>Antall: {event.quantity}</span>
                  {' - '}
                  <span className="text-gray-500">Operatør: {event.operatorId}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationActivityTest;
